import React from "react";
import styled from "@emotion/styled";
import { CustomModal, ModalTitleWithLastModifiedDate } from "../../utils/StyledComponents";
import { PromoFormProps } from "../promos";
import { Checkbox, Form, Input, Select, Spin, Typography } from "antd";
import { getDurationOnly } from "../../utils";
import { PROMO_TYPE_OPTIONS } from "../promo-types";
import useDistributionData from "../../distribution_partner/hooks/useDistributionData";

const { Item } = Form;

// Two columns via CSS grid, not antd Row gutters: gutters add negative side
// margins, and CustomModal's body has no padding to absorb them, which
// produced a horizontal scrollbar and clipped the left-hand labels.
const PromoFormLayout = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 1fr) 280px;
	gap: 24px;

	& .field-pair {
		display: grid;
		grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
		gap: 16px;
	}
`;

// Right-hand platform panel: header row stays put, the checkbox list scrolls.
const PlatformPanel = styled.div`
	height: 100%;
	display: flex;
	flex-direction: column;
	border-left: 1px solid rgba(5, 5, 5, 0.06);
	padding-left: 24px;
	min-width: 0;

	& .platform-header {
		display: flex;
		justify-content: space-between;
		align-items: baseline;
		margin-bottom: 4px;
	}

	& .platform-list {
		max-height: 340px;
		overflow-y: auto;
		padding: 8px 0;
		border-top: 1px solid rgba(5, 5, 5, 0.06);
	}

	& .platform-list .ant-checkbox-wrapper {
		display: flex;
		padding: 4px 0;
	}
`;

const AddEditPromoModal: React.FC<PromoFormProps> = ({
	form,
	loading,
	promoEditId,
	onOkPromo,
	onCancelPromo,
}) => {
	const { distributionPlainOptions, distributionPlatformIds } = useDistributionData();
	const selectedPlatforms: string[] = Form.useWatch("distribution_platform_ids", form) ?? [];
	const allSelected =
		distributionPlatformIds.length > 0 && selectedPlatforms.length === distributionPlatformIds.length;
	const someSelected = selectedPlatforms.length > 0 && !allSelected;

	return (
		<CustomModal
			centered
			open={true}
			width={760}
			onCancel={onCancelPromo}
			onOk={onOkPromo}
			title={
				promoEditId ? (
					<ModalTitleWithLastModifiedDate
						title="Update Promo Info..."
						lastModifiedDate={form.getFieldValue("updated_at")}
					/>
				) : (
					"Add Promo"
				)
			}
			okText={promoEditId ? "Update" : "Create"}
			okButtonProps={{ loading }}
			cancelButtonProps={{ disabled: loading }}
			maskClosable={false}
		>
			<Spin spinning={loading}>
				<Form form={form} layout="vertical" requiredMark={false}>
					<PromoFormLayout>
						<div>
							<Item
								name="type"
								label="Type"
								rules={[{ required: true, message: "This is required" }]}
							>
								<Select options={PROMO_TYPE_OPTIONS} placeholder="Promo Type" />
							</Item>
							<Item name="ref_id" label="Ref ID">
								<Input placeholder="Episode Ref ID" />
							</Item>
							<Item
								name="promo_url"
								label="Promo URL"
								rules={[{ required: true, message: "This is required" }]}
							>
								<Input placeholder="Promo URL Link" />
							</Item>
							<div className="field-pair">
								<Item
									label="Duration"
									name="duration"
									rules={[{ required: true, message: "This is required" }]}
								>
									<Input
										placeholder="Duration"
										maxLength={12}
										onChange={e => form.setFieldValue("duration", getDurationOnly(e.target.value))}
									/>
								</Item>
								<Item name="amagi_id" label="Amagi ID">
									<Input placeholder="Amagi ID" />
								</Item>
							</div>
						</div>
						<PlatformPanel>
							<div className="platform-header">
								<Typography.Text strong>Distribution Platforms</Typography.Text>
								<Typography.Text type="secondary">
									{selectedPlatforms.length} of {distributionPlatformIds.length}
								</Typography.Text>
							</div>
							<Typography.Text type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
								Amagi OTA Break Fill only uses promos tagged with the schedule&apos;s platform.
							</Typography.Text>
							<Checkbox
								indeterminate={someSelected}
								checked={allSelected}
								onChange={e =>
									form.setFieldValue(
										"distribution_platform_ids",
										e.target.checked ? distributionPlatformIds : []
									)
								}
								style={{ marginBottom: 8 }}
							>
								{allSelected ? "De-Select All" : "Select All"}
							</Checkbox>
							<div className="platform-list">
								<Item name="distribution_platform_ids" noStyle>
									<Checkbox.Group style={{ display: "flex", flexDirection: "column", width: "100%" }}>
										{distributionPlainOptions.map((platform: { value: string; label: string }) => (
											<Checkbox key={platform.value} value={platform.value}>
												{platform.label}
											</Checkbox>
										))}
									</Checkbox.Group>
								</Item>
							</div>
						</PlatformPanel>
					</PromoFormLayout>
				</Form>
			</Spin>
		</CustomModal>
	);
};

export default AddEditPromoModal;
